import { delay } from "./registry";
import { DOCTOR_REQUIRED_MESSAGE } from "../../encounterStart";
import { emptySoapNote } from "../../types/encounter";
import type {
  Diagnosis,
  EncounterAttachment,
  EncounterDetail,
  EncounterListFilter,
  EncounterPrescriptionItem,
  EncounterSummary,
  SoapNote,
  Vitals,
} from "../../types/encounter";
import type { PageResponse } from "../../types/patient";

/* ------------------------------------------------------------------ */
/* Store                                                              */
/* ------------------------------------------------------------------ */

interface StoredEncounter extends EncounterDetail {}

const STORE: StoredEncounter[] = [];

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - 30);
  return d.toISOString();
}
function todayISOPlus(minutes: number) {
  const d = new Date();
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
}

function id(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function emptyVitals(): Vitals {
  return {
    systolicBp: null,
    diastolicBp: null,
    heartRate: null,
    temperatureC: null,
    respiratoryRate: null,
    spO2: null,
    weightKg: null,
    heightCm: null,
    recordedAt: null,
  };
}

function emptySoap(): SoapNote {
  return emptySoapNote();
}

// Seed: 4 encounters across statuses
(function seed() {
  STORE.push({
    id: id("enc"),
    patientId: "pat-BN-AB12CD34",
    patientName: "Đỗ Văn Giang",
    patientCode: "BN-AB12CD34",
    roomId: "room-03",
    roomName: "PK Nội Tim Mạch 2 (Phòng 03)",
    roomNo: 1,
    registrationNo: 7,
    departmentName: "Khám bệnh",
    appointmentStartAt: new Date(Date.now() - 20 * 60000).toISOString(),
    patientPhone: "+84967369125",
    patientGender: "M",
    patientAge: 36,
    doctorId: "doc-001",
    doctorName: "BS. Đỗ Văn Giang",
    appointmentId: null,
    startedAt: todayISOPlus(-45),
    completedAt: null,
    status: "IN_PROGRESS",
    chiefComplaint: "Đau thượng vị 3 ngày, ợ chua, ăn khó tiêu",
    diagnosisSummary: null,
    patientSelfDescription:
      "Em bị đau bụng vùng trên rốn mấy hôm nay, hay ợ chua, ăn vào thấy khó tiêu ạ.",
    bookingSource: "PATIENT",
    publicToken: "mocktoken-ab12cd34",
    orderCode: "S26232798",
    hasPrescription: false,
    hasVitals: true,
    vitals: {
      systolicBp: 128,
      diastolicBp: 82,
      heartRate: 76,
      temperatureC: 36.8,
      respiratoryRate: 18,
      spO2: 98,
      weightKg: 68,
      heightCm: 172,
      recordedAt: todayISOPlus(-40),
    },
    soap: {
      ...emptySoapNote(todayISOPlus(-25)),
      history: {
        chiefComplaint: "Đau thượng vị",
        presentIllness:
          "Đau thượng vị 3 ngày, ợ chua, ăn khó tiêu. Không nôn, không sốt.",
        personalHistory: "Tiền sử HP (+) cách 2 năm.",
        familyHistory: "",
      },
      exam: {
        ...emptySoapNote().exam,
        status: "Tỉnh, tiếp xúc tốt.",
        general: "Mạch 76, HA 128/82, T° 36.8.",
        abdomen: "Bụng mềm, ấn thượng vị đau nhẹ.",
      },
      assessment: {
        ...emptySoapNote().assessment,
        preliminaryProblem: "Theo dõi viêm dạ dày tái phát.",
        preliminaryPlan:
          "Esomeprazol 40mg + Smecta. Tránh rượu bia, cà phê. Tái khám 7 ngày.",
      },
    },
    diagnoses: [],
    prescription: { signed: false, signedAt: null, items: [], pdfUrl: null },
    attachments: [],
  });
  STORE.push({
    id: id("enc"),
    patientId: "pat-BN-EF56GH78",
    patientName: "Nguyễn Thị Lan",
    patientCode: "BN-EF56GH78",
    roomId: "room-04",
    roomName: "PK Nội Tổng Hợp (Phòng 08)",
    roomNo: 2,
    registrationNo: 11,
    departmentName: "Khám bệnh",
    appointmentStartAt: new Date(Date.now() - 20 * 60000).toISOString(),
    patientPhone: "+84934567890",
    patientGender: "F",
    patientAge: 30,
    doctorId: "doc-002",
    doctorName: "ThS.BS. Nguyễn Thị Hồng",
    appointmentId: null,
    startedAt: todayISO(),
    completedAt: null,
    status: "IN_PROGRESS",
    chiefComplaint: "Ho khan 1 tuần, không sốt",
    diagnosisSummary: null,
    patientSelfDescription: null,
    bookingSource: "STAFF",
    publicToken: "mocktoken-ef56gh78",
    orderCode: "S26232799",
    hasPrescription: false,
    hasVitals: false,
    vitals: emptyVitals(),
    soap: emptySoap(),
    diagnoses: [],
    prescription: { signed: false, signedAt: null, items: [], pdfUrl: null },
    attachments: [],
  });
  // Signed encounter from earlier today
  const completedAt = (() => {
    const d = new Date();
    d.setHours(d.getHours() - 2);
    return d.toISOString();
  })();
  STORE.push({
    id: id("enc"),
    patientId: "pat-BN-JK90LM12",
    patientName: "Trần Văn Hùng",
    patientCode: "BN-JK90LM12",
    // #19040 — ca chưa xếp phòng: null, để giao diện nói "Chưa xếp phòng" thay vì in ra undefined.
    roomId: null,
    roomName: null,
    roomNo: null,
    registrationNo: null,
    departmentName: null,
    appointmentStartAt: null,
    patientPhone: "+84912345678",
    patientGender: "M",
    patientAge: 47,
    doctorId: "doc-001",
    doctorName: "BS. Đỗ Văn Giang",
    appointmentId: null,
    startedAt: (() => {
      const d = new Date();
      d.setHours(d.getHours() - 3);
      return d.toISOString();
    })(),
    completedAt,
    status: "SIGNED",
    chiefComplaint: "Tái khám tăng huyết áp",
    diagnosisSummary: "Tăng huyết áp vô căn (I10)",
    patientSelfDescription: null,
    bookingSource: "AI_ASSISTANT",
    publicToken: "mocktoken-jk90lm12",
    orderCode: "S26232800",
    hasPrescription: true,
    hasVitals: true,
    vitals: {
      systolicBp: 142,
      diastolicBp: 88,
      heartRate: 72,
      temperatureC: 36.5,
      respiratoryRate: 17,
      spO2: 98,
      weightKg: 74,
      heightCm: 168,
      recordedAt: (() => {
        const d = new Date();
        d.setHours(d.getHours() - 3);
        return d.toISOString();
      })(),
    },
    soap: {
      ...emptySoapNote(completedAt),
      history: {
        chiefComplaint: "Tái khám tăng huyết áp",
        presentIllness: "Tuân thủ thuốc, không tác dụng phụ.",
        personalHistory: "",
        familyHistory: "",
      },
      exam: {
        ...emptySoapNote().exam,
        status: "Tỉnh.",
        cardio: "HA 142/88, mạch 72, đều, T1 T2 rõ.",
      },
      assessment: {
        ...emptySoapNote().assessment,
        preliminaryProblem: "HA tăng nhẹ - cần điều chỉnh liều.",
        definitiveDiagnosis: "Tăng huyết áp vô căn, kiểm soát chưa đạt.",
        definitivePlan:
          "Tăng Amlodipine 5mg → 10mg. Tái khám 4 tuần. Đo HA tại nhà mỗi sáng.",
      },
    },
    diagnoses: [
      { id: id("dx"), icd10Code: "I10", label: "Tăng huyết áp vô căn", primary: true, note: null },
    ],
    prescription: {
      signed: true,
      signedAt: completedAt,
      pdfUrl: "/mock/prescription.pdf",
      items: [
        {
          id: id("rx"),
          medicineId: "med-amlodipine-10",
          medicineName: "Amlodipine 10mg",
          medicineUnit: "viên",
          medicineStrength: "10mg",
          quantity: 30,
          doseMorning: 1,
          doseNoon: 0,
          doseAfternoon: 0,
          doseEvening: 0,
          doseUnit: "viên",
          route: "ORAL",
          mealTiming: "AFTER_MEAL",
          note: "Uống mỗi sáng sau ăn",
        },
      ],
    },
    attachments: [],
  });
})();

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function summaryOf(e: StoredEncounter): EncounterSummary {
  const {
    vitals: _v,
    soap: _s,
    diagnoses: _d,
    prescription: _p,
    attachments: _a,
    ...summary
  } = e;
  void _v; void _s; void _d; void _p; void _a;
  return {
    ...summary,
    diagnosisSummary:
      e.diagnoses.length > 0
        ? e.diagnoses.map((d) => d.label).join(", ")
        : null,
    hasPrescription: e.prescription.items.length > 0,
    hasVitals: !!e.vitals.recordedAt,
  };
}

function matchesFilter(e: StoredEncounter, f: EncounterListFilter | undefined): boolean {
  if (!f) return true;
  if (f.q && f.q.trim()) {
    const q = f.q.trim().toLowerCase();
    const hay = `${e.patientName} ${e.patientCode ?? ""} ${e.patientPhone ?? ""} ${e.doctorName} ${e.chiefComplaint ?? ""}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.status && e.status !== f.status) return false;
  if (f.doctorId && e.doctorId !== f.doctorId) return false;
  if (f.fromDate && e.startedAt < f.fromDate) return false;
  if (f.toDate) {
    const end = `${f.toDate}T23:59:59.999`;
    if (e.startedAt > end) return false;
  }
  return true;
}

/* ------------------------------------------------------------------ */
/* Public mock API                                                    */
/* ------------------------------------------------------------------ */

/**
 * Chỉ định CLS/dịch vụ per encounter (#18839). The mock has to store these now: they used to be
 * readable back out of the SOAP note the workspace dual-wrote, and that dual-write is exactly what
 * was deleting the doctor's typed results — so a no-op here would mean the click-through UI drops
 * every order the moment it is saved.
 */
const SERVICE_ORDERS = new Map<
  string,
  { id: string; serviceId: string | null; serviceName: string; note: string | null }[]
>();

export const mockEncounters = {
  async listServiceOrders(
    encounterId: string
  ): Promise<{ id: string; serviceId: string | null; serviceName: string; note: string | null }[]> {
    return SERVICE_ORDERS.get(encounterId) ?? [];
  },

  async replaceServiceOrders(
    encounterId: string,
    orders: { serviceId: string | null; serviceName: string; note?: string | null }[]
  ): Promise<unknown> {
    SERVICE_ORDERS.set(
      encounterId,
      orders.map((o, i) => ({
        id: `so-${encounterId}-${i}`,
        serviceId: o.serviceId,
        serviceName: o.serviceName,
        note: o.note ?? null,
      }))
    );
    return null;
  },

  async list(
    filter: EncounterListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<EncounterSummary>> {
    const all = STORE.filter((e) => matchesFilter(e, filter));
    const direction = (pageable.sort ?? "").endsWith(",asc") ? 1 : -1;
    all.sort((a, b) => direction * b.startedAt.localeCompare(a.startedAt));
    const size = pageable.size ?? 20;
    const page = pageable.page ?? 0;
    const start = page * size;
    return delay({
      content: all.slice(start, start + size).map(summaryOf),
      totalElements: all.length,
      totalPages: Math.max(1, Math.ceil(all.length / size)),
      number: page,
      size,
    });
  },

  async queue(): Promise<EncounterSummary[]> {
    return delay(
      STORE.filter((e) => e.status === "IN_PROGRESS")
        .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
        .map(summaryOf)
    );
  },

  async get(encounterId: string): Promise<EncounterDetail> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    return delay({ ...e, diagnoses: [...e.diagnoses], attachments: [...e.attachments] });
  },

  async createFromAppointment(input: {
    appointmentId: string;
    patientId: string;
    patientName: string;
    patientCode: string | null;
    patientPhone: string | null;
    patientGender: "M" | "F" | "OTHER" | "UNKNOWN" | null;
    patientAge: number | null;
    // Mirrors the real client (#18899): a doctor may legitimately be absent.
    doctorId: string | null;
    doctorName: string | null;
    chiefComplaint?: string | null;
  }): Promise<EncounterDetail> {
    // The server refuses an encounter with no doctor (422 appointment_doctor_required since the
    // #18899 follow-up). The mock has to refuse too: accepting it here would let the mock-backed
    // UI walk a path the real API rejects, which is the kind of divergence #18899 was reported
    // through. The message is the shared one, so the mock cannot drift from the real wording.
    if (!input.doctorId) {
      throw new Error(DOCTOR_REQUIRED_MESSAGE);
    }
    const fresh: StoredEncounter = {
      // #19040 — một ca mở trực tiếp chưa có ngữ cảnh phòng; null là câu trả lời đúng.
      roomId: null,
      roomName: null,
      roomNo: null,
      registrationNo: null,
      departmentName: null,
      appointmentStartAt: null,
      id: id("enc"),
      patientId: input.patientId,
      patientName: input.patientName,
      patientCode: input.patientCode,
      patientPhone: input.patientPhone,
      patientGender: input.patientGender,
      patientAge: input.patientAge,
      doctorId: input.doctorId,
      doctorName: input.doctorName ?? "",
      appointmentId: input.appointmentId,
      startedAt: new Date().toISOString(),
      completedAt: null,
      status: "IN_PROGRESS",
      chiefComplaint: input.chiefComplaint ?? null,
      diagnosisSummary: null,
      patientSelfDescription: null,
      bookingSource: "STAFF",
      publicToken: null,
      orderCode: null,
      hasPrescription: false,
      hasVitals: false,
      vitals: emptyVitals(),
      soap: emptySoap(),
      diagnoses: [],
      prescription: { signed: false, signedAt: null, items: [], pdfUrl: null },
      attachments: [],
    };
    STORE.push(fresh);
    return delay(fresh);
  },

  async updateVitals(encounterId: string, v: Vitals): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    e.vitals = { ...v, recordedAt: new Date().toISOString() };
    e.hasVitals = true;
    return delay(undefined);
  },
  async getExamMode(_encounterId: string) {
    return delay({ examType: "SERVICE" as const, bhytCardNo: null, locked: false });
  },
  async updateExamMode(
    _encounterId: string,
    req: { examType: "SERVICE" | "BHYT"; bhytCardNo: string | null },
  ) {
    return delay({ examType: req.examType, bhytCardNo: req.bhytCardNo, locked: false });
  },

  async updateSoap(encounterId: string, soap: Omit<SoapNote, "updatedAt">): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    e.soap = { ...soap, updatedAt: new Date().toISOString() };
    return delay(undefined, 120);
  },

  async updateChiefComplaint(encounterId: string, chiefComplaint: string | null): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    e.chiefComplaint = chiefComplaint;
    return delay(undefined);
  },

  async replaceDiagnoses(encounterId: string, diagnoses: Omit<Diagnosis, "id">[]): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    e.diagnoses = diagnoses.map((d, i) => ({ ...d, id: id(`dx-${i}`) }));
    return delay(undefined);
  },

  async replacePrescription(
    encounterId: string,
    items: Omit<EncounterPrescriptionItem, "id">[]
  ): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    e.prescription.items = items.map((it, i) => ({
      ...it,
      id: `${encounterId}-rx-${i}`,
    }));
    return delay(undefined);
  },

  async signEncounter(encounterId: string): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    if (e.diagnoses.length === 0) {
      throw new Error("Phải có ít nhất 1 chẩn đoán trước khi ký.");
    }
    e.status = "SIGNED";
    e.completedAt = new Date().toISOString();
    e.prescription.signed = e.prescription.items.length > 0;
    if (e.prescription.signed) {
      e.prescription.signedAt = e.completedAt;
      e.prescription.pdfUrl = `/mock/prescription/${e.id}.pdf`;
    }
    return delay(undefined);
  },

  async remove(encounterId: string): Promise<void> {
    const i = STORE.findIndex((x) => x.id === encounterId);
    if (i >= 0) STORE.splice(i, 1);
    return delay(undefined);
  },

  async createFollowUp(
    _id: string,
    date: string
  ): Promise<{
    appointmentId: string;
    date: string;
    examFeeAmount: number;
    examFeeStatus: string;
  }> {
    return delay({
      appointmentId: `apt-${Math.random().toString(36).slice(2, 8)}`,
      date,
      examFeeAmount: 150_000,
      examFeeStatus: "UNPAID",
    });
  },

  async transferDepartment(
    _id: string,
    input: {
      departmentId: string;
      doctorProfileId?: string | null;
      roomId?: string | null;
      reason?: string | null;
    }
  ): Promise<{
    appointmentId: string;
    departmentId: string;
    departmentName: string;
    examFeeAmount: number;
    examFeeStatus: string;
  }> {
    return delay({
      appointmentId: `apt-${Math.random().toString(36).slice(2, 8)}`,
      departmentId: input.departmentId,
      departmentName: "Khoa (mock)",
      examFeeAmount: 150_000,
      examFeeStatus: "PENDING",
    });
  },

  async cancelEncounter(encounterId: string, reason: string): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    e.status = "CANCELLED";
    e.completedAt = new Date().toISOString();
    if (!e.soap.assessment.definitivePlan.includes("[HỦY]")) {
      e.soap = {
        ...e.soap,
        assessment: {
          ...e.soap.assessment,
          definitivePlan:
            `${e.soap.assessment.definitivePlan}\n\n[HỦY ${new Date().toISOString()}] ${reason}`.trim(),
        },
        updatedAt: new Date().toISOString(),
      };
    }
    return delay(undefined);
  },

  async uploadAttachment(
    encounterId: string,
    file: File,
    category: EncounterAttachment["category"]
  ): Promise<EncounterAttachment> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) throw new Error("Không tìm thấy lần khám");
    const fresh: EncounterAttachment = {
      id: `att-${Math.random().toString(36).slice(2, 8)}`,
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      uploadedAt: new Date().toISOString(),
      uploadedBy: "Lễ tân",
      category,
      status: "READY",
      downloadable: true,
      patientVisible: false,
    };
    e.attachments.push(fresh);
    return delay(fresh);
  },

  async downloadAttachment(
    _encounterId: string,
    _attachmentId: string,
    _fileName: string
  ): Promise<void> {
    return delay(undefined);
  },

  async setAttachmentPatientVisible(
    _encounterId: string,
    _attachmentId: string,
    _visible: boolean
  ): Promise<void> {
    await delay(undefined);
  },

  async removeAttachment(encounterId: string, attachmentId: string): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (!e) return delay(undefined);
    e.attachments = e.attachments.filter((a) => a.id !== attachmentId);
    return delay(undefined);
  },

  async revokeShareLink(encounterId: string): Promise<void> {
    const e = STORE.find((x) => x.id === encounterId);
    if (e) e.publicToken = null;
    return delay(undefined);
  },

  async renewShareLink(
    encounterId: string
  ): Promise<{ publicToken: string; orderCode: string; expiresAt: string }> {
    const e = STORE.find((x) => x.id === encounterId);
    const publicToken = `mock-${Math.random().toString(36).slice(2)}`;
    if (e) e.publicToken = publicToken;
    return delay({
      publicToken,
      orderCode: e?.orderCode ?? "S00000000",
      expiresAt: new Date(Date.now() + 7 * 864e5).toISOString(),
    });
  },
};

